import { theme } from "ui/theme";

import teacherAudioReflectionCardImages from "images/CardImages/teacherAudioReflectionCardImages";
import teacherTextReflectionCardImages from "images/CardImages/teacherTextReflectionCardImages";
import { checksum } from "utils/checksum";

const beautyMarks: {
  [key: string]: {
    images?: string[];
    full_images?: string[];
    backgrounds?: string[];
  };
} = {
  teacherAudioReflection: teacherAudioReflectionCardImages,
  teacherTextReflection: teacherTextReflectionCardImages,
};

type CardType = "teacherAudioReflection" | "teacherTextReflection";

type GetBeautyMarkProps = {
  cardType: CardType;
  id: string;
  fullSize?: boolean;
};

function getImageKey({ fullSize }: { fullSize?: boolean }) {
  if (fullSize) {
    return "full_images";
  }

  return "images";
}

export default function getBeautyMark({
  cardType,
  id,
  fullSize = false,
}: GetBeautyMarkProps) {
  const key = getImageKey({ fullSize });
  const imageHash = beautyMarks[cardType][key];
  if (!imageHash) {
    // @assert - this case should never occur.
    throw new Error("image URL not found.");
  }

  const beautyMarkId = getBeautyMarkId(id, 10);
  const beautyMarkUrl = imageHash[beautyMarkId];
  const beautyMarkColor = getBeautyMarkImageColor(cardType, beautyMarkId);

  return {
    url: beautyMarkUrl,
    color: beautyMarkColor,
  };
}

/**
 * Gets the color associated with a beauty mark image. This should be the dominate
 * color in the image, but in cases where the image is evenly split between two
 * colors, the associated color is somewhat random.
 */
const getBeautyMarkImageColor = (cardType: CardType, id: number) => {
  const idToColorMapTeacher = [
    theme.ColorCarrot500,
    theme.ColorSpace500,
    theme.ColorPine,
    theme.ColorCarrot500,
    theme.ColorBlurple500,
    theme.ColorMint500,
    theme.ColorSpace500,
    theme.ColorMint500,
    theme.ColorSun500,
    theme.ColorSun500,
  ];

  switch (cardType) {
    case "teacherAudioReflection" || "teacherTextReflection":
      return idToColorMapTeacher[id];
    default:
      return theme.ColorSpace500;
  }
};

const beautyMarkColors = [
  {
    backgroundColor: theme.ColorPine500,
    color: theme.ColorWhite,
  },
  {
    backgroundColor: theme.ColorBlurple500,
    color: theme.ColorWhite,
  },
  {
    backgroundColor: theme.ColorSpace500,
    color: theme.ColorWhite,
  },
  {
    backgroundColor: theme.ColorSun500,
    color: theme.ColorSpace500,
  },
  {
    backgroundColor: theme.ColorMint500,
    color: theme.ColorSpace500,
  },
  {
    backgroundColor: theme.ColorLemon500,
    color: theme.ColorSpace500,
  },
  {
    backgroundColor: theme.ColorGum500,
    color: theme.ColorSpace500,
  },
  {
    backgroundColor: theme.ColorSky500,
    color: theme.ColorSpace500,
  },
];

export function getBeautyMarkColor(id: string) {
  return beautyMarkColors[getBeautyMarkId(id, beautyMarkColors.length)];
}

function getBeautyMarkId(id: string, numOfVariations: number) {
  return checksum(id) % numOfVariations;
}
